import { AfterViewInit, Component, ElementRef, inject, OnInit, viewChild, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RegisterRequest, SingInRequest } from '../../../core/models/auth/auth.request';
import { BaseComponent } from '../../../core/shared/base.component';
import Swal from 'sweetalert2';
import { LoaderService } from '../../../core/shared/services/loader.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent extends BaseComponent implements OnInit, AfterViewInit {
  loader = inject(LoaderService);
  route = inject(ActivatedRoute);
  isRegister: boolean = false;
  form: FormGroup;
  singInForm: FormGroup;
  passwordVisible = false;
  confirmPasswordVisible = false;
  passwordsMatch = false;
  appVersion: string | null = null;
  hasVersion: boolean = false;

  pointer = viewChild<ElementRef>("pointer");

  constructor(private router: Router, private authService: AuthService, private fb: FormBuilder) {
    super()


    const StrongPasswordRegx: RegExp = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/;

    this.form = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(20), Validators.pattern(StrongPasswordRegx)]],
      confirmPassword: ['', [Validators.required]],
      acceptTerms: [true, Validators.requiredTrue]
    }, { validators: this.passwordMatchValidator });
    this.singInForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      acceptTerms: [true, Validators.requiredTrue]
    });
  }
  async ngOnInit(): Promise<void> {
    this.loader.show();
    this.loadVersion();
    this.route.queryParams.subscribe(async (params) => {
      let redirectUrl = params['redirect'] || '/auth/redirect-to';
      
      // Verificar si se debe activar la pestaña de registro
      if (params['activeTab'] === 'register') {
        this.isRegister = true;
      }
      
      if (this.authService.isLoggedIn()) {
        await this.router.navigate([redirectUrl]);
      }
      await this.sleep(2000);
      this.loader.hide();
    });
  }

  loadVersion(): void {
    const savedVersion = localStorage.getItem('appVersion');
    if (savedVersion) {
      this.appVersion = savedVersion;
      this.hasVersion = true;
    } else {
      this.hasVersion = false;
    }
  }

  clearCookiesAndReload(): void {
    // Limpiar todas las cookies
    document.cookie.split(';').forEach(c => {
      const eqPos = c.indexOf('=');
      const name = eqPos > -1 ? c.substring(0, eqPos).trim() : c.trim();
      if (name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      }
    });
    // Recargar la página
    window.location.reload();
  }



  ngAfterViewInit(): void {
    console.log('ngAfterViewInit');
    // this.pointer()!.nativeElement.style.width = 0;
  }
  get registerName() {
    return this.form.get('name');
  }
  get registerEmail() {
    return this.form.get('email');
  }
  get registerPassword() {
    return this.form.get('password');
  }

  get registerConfirmPassword() {
    return this.form.get('confirmPassword');
  }

  get singInUsername() {
    return this.singInForm.get('username');
  }

  get singInPassword() {
    return this.singInForm.get('password');
  }

  get singInAcceptTerms() {
    return this.singInForm.get('acceptTerms');
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleConfirmPasswordVisibility() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  checkPasswordsMatch() {
    const password = this.form.get('password')?.value;
    const confirmPassword = this.form.get('confirmPassword')?.value;
    
    if (password && confirmPassword && password === confirmPassword) {
      this.passwordsMatch = true;
    } else {
      this.passwordsMatch = false;
    }
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirmPassword && confirmPassword.errors) {
        delete confirmPassword.errors['passwordMismatch'];
        if (Object.keys(confirmPassword.errors).length === 0) {
          confirmPassword.setErrors(null);
        }
      }
      return null;
    }
  }

  showRegisterForm() {
    this.isRegister = true;
  }

  showLoginForm() {
    this.isRegister = false;
  }

  toggleTab(isRegister: boolean) {
    this.isRegister = isRegister;
  }

  register() {
    if (this.form?.valid) {

      let param = this.form?.value as RegisterRequest;

      this.loader.show(); 
      this.authService.register(param)
        .subscribe({
          next: async (res: any) => {
            console.log('res', res);
            await this.router.navigate(['/auth/verification'], { queryParams: { jwtUser: res.data.accessToken } });
            this.loader.hide();
          },
          error: err => {
            console.log('err', err);
            this.loader.hide();
            if (err.status === 400 && err.error.message.includes('Ya existe un usuario registrado')) {
              this.alert(`Ya existe un usuario con el correo: <strong>${param.email}</strong>`, "");
              this.form.setValue({ email: '', password: '', name: '' });
            } else if (err.status === 400 && err.error.error === 'email_register_with_google') {
              // Mostrar modal específico para registro con formulario cuando ya existe con Google
              Swal.fire({
                title: 'Verifica tu forma de ingreso',
                html: 'Te registraste con tu cuenta de Google. Para ingresar a Phoenix, usa el botón <img src="assets/icons/google-logo.png" alt="Google" style="width: 20px; height: 20px; vertical-align: middle; margin: 0 5px;">. No es necesario ingresar tu correo ni contraseña.',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#e74c3c',
                allowOutsideClick: false,
                allowEscapeKey: false
              }).then(() => {
                this.form.setValue({ email: '', password: '', name: '', confirmPassword: '' });
              });
            } else {
              this.errorDefault();
            }
          },
          complete: () => {

          }
        });
    }



  }

  onLocalLogin(): void {
    if (this.singInForm?.valid) {
      console.log('Form submitted:', this.singInForm?.value);
      let param = this.singInForm?.value as SingInRequest;

      this.loader.show();
      this.authService.signin(param)
        .subscribe({
          next: async (res: any) => {
            console.log('res', res);
            // const authUser: AuthUser = jwtDecode(res.data.accessToken);
            if (res.data.isNewUser)
              await this.router.navigate(['/auth/verification'], { queryParams: { jwtUser: res.data.accessToken } });
            else {
              await this.router.navigate(['/auth/redirect'], { queryParams: { jwtUser: res.data.accessToken } })
            }
            this.loader.hide();
          },
          error: err => {
            this.loader.hide();
            console.log('🔍 Error completo recibido:', err);
            console.log('🔍 Status:', err.status);
            console.log('🔍 Error object:', err.error);
            console.log('🔍 Error error property:', err.error?.error);
            
            if (err.status == 401) {
              this.error(err.error.message);
            } else if (err.status === 400 && err.error?.message === 'email_register_with_google') {
              console.log('✅ Mostrando modal de validación cruzada');
              // Mostrar modal específico para inicio de sesión con formulario cuando ya existe con Google
              Swal.fire({
                title: 'Verifica tu forma de ingreso',
                html: 'Te registraste con tu cuenta de Google. Para ingresar a Phoenix, usa el botón <img src="assets/icons/google-logo.png" alt="Google" style="width: 20px; height: 20px; vertical-align: middle; margin: 0 5px;">. No es necesario ingresar tu correo ni contraseña.',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#e74c3c',
                allowOutsideClick: false,
                allowEscapeKey: false
              }).then(() => {
                this.singInForm.setValue({ username: '', password: '', acceptTerms: true });
              });
            } else {
              console.log('❌ Error no manejado, mostrando error por defecto');
              this.errorDefault();
            }
          },
          complete: () => {
          }
        });
    }
  }

  onGoogleLogin(): void {
    this.loader.show();
    window.location.assign(`${environment.apiUrl}/auth/google?action=login`);
  }
  onGoogleRegister(): void {
    this.loader.show();
    window.location.assign(`${environment.apiUrl}/auth/google?action=register`);
  }

  checkPassword($event: any) {
    console.log('$event', $event);

    let pointer = document.getElementById("password-pointer");
    if (!pointer) return;

    let point = 0;
    let value = $event.target.value;
    let widthPower =
      ["10%", "25%", "50%", "75%", "100%"];
    let colorPower =
      ["#D73F40", "#DC6551", "#F2B84F", "#BDE952", "#3ba62f"];

    if (value.length >= 8) {
      let arrayTest =
        [/[0-9]/, /[a-z]/, /[A-Z]/, /(?=.*\W)/,];
      arrayTest.forEach((item) => {
        if (item.test(value)) {
          point += 1;
        }
      });
    }
    pointer.style.width = widthPower[point];
    pointer.style.backgroundColor = colorPower[point];
  }

  navigateToRecoverPassword() {
    this.router.navigate(['/auth/recover-password']);
  }

  redirectLinkedIn() {
    this.loader.show();
    window.location.assign(`${environment.apiUrl}/auth/linkedin?action=login`);
  }

  redirectTermsConditions() {
    this.router.navigate(['/terms-conditions']);
  }

  redirectPrivacyPolicies() {
    this.router.navigate(['/privacy-policies']);
  }
}
